import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import type {FloorplanLocateRequest} from "../hooks/useReadStore";
import {ZOOM_MAX, ZOOM_MIN} from "./constants";

interface FloorplanLocateObject {
    getBoundingRect: () => {left: number; top: number; width: number; height: number};
}

interface FloorplanLocateCanvas {
    getObjects: () => FloorplanLocateObject[];
    setViewportTransform: (transform: [number, number, number, number, number, number]) => void;
    renderAll: () => void;
}

interface FloorplanLocateContainer {
    clientWidth: number;
    clientHeight: number;
}

interface ApplyFloorplanLocateParams {
    request: FloorplanLocateRequest;
    canvas: FloorplanLocateCanvas;
    container: FloorplanLocateContainer;
    isMobile: boolean;
    selectTimeframe: (range: {start: string; end: string}) => void;
    highlightTables: (tableIds: string[]) => void;
}

export function applyFloorplanLocate({
    request,
    canvas,
    container,
    isMobile,
    selectTimeframe,
    highlightTables,
}: ApplyFloorplanLocateParams) {
    if (!request.start || !request.end) return;

    selectTimeframe({
        start: formatDateHM(request.start),
        end: formatDateHM(request.end),
    });

    const objects = canvas.getObjects();
    if (objects.length === 0) return;

    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    objects.forEach(object => {
        const bounds = object.getBoundingRect();
        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.left + bounds.width);
        maxY = Math.max(maxY, bounds.top + bounds.height);
    });

    const padding = isMobile ? 0 : 50;
    const scale = Math.min(
        (container.clientWidth - padding * 2) / (maxX - minX),
        (container.clientHeight - padding * 2) / (maxY - minY),
        2,
    );
    const zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale * 100)) / 100;

    canvas.setViewportTransform([
        zoom,
        0,
        0,
        zoom,
        container.clientWidth / 2 - ((minX + maxX) / 2) * zoom,
        container.clientHeight / 2 - ((minY + maxY) / 2) * zoom,
    ]);
    canvas.renderAll();
    highlightTables(request.tableIds);
}
